import React, { useState, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  Chart as ChartJS,
  ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, BarElement,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  getCol, parseMulti, countValues, topN,
  computeMetrics, computeRatings, computePolicies, buildInsightPrompt, generateLocalInsights,
} from './utils';
import './App.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// ─── Colors ──────────────────────────────────────────────────────────────────
const C = {
  orange: '#E07C3E',
  navy: '#1A3A5C',
  blue: '#2563EB',
  blue2: '#1D4ED8',
  green: '#059669',
  green2: '#10B981',
  amber: '#D97706',
  amber2: '#F59E0B',
  coral: '#DC2626',
  coral2: '#EF4444',
  purple: '#7C3AED',
  teal: '#0D9488',
  gray: '#6B7280',
  indigo: '#4F46E5',
  pink: '#DB2777',
};
const PIE_COLORS = [C.blue, C.orange, C.green2, C.purple, C.teal, C.amber2, C.coral2, C.indigo, C.pink, C.gray];

// ─── SVG Icons ───────────────────────────────────────────────────────────────
const Icons = {
  overview: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  ai: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"/><path d="M16 15a4 4 0 0 1 4 4v2H4v-2a4 4 0 0 1 4-4"/><circle cx="12" cy="10" r="1"/></svg>,
  delivery: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  performance: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg>,
  policy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  coverage: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  insights: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9.663 17h4.674M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  target: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  api: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
  cost: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  quality: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  kb: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
};

// ─── Sidebar nav items ───────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: 'overview', label: 'Overview', icon: Icons.overview },
  { id: 'section-a', label: 'A: Organization Profile', icon: Icons.users },
  { id: 'section-b', label: 'B: AI Models & Delivery', icon: Icons.ai },
  { id: 'section-c', label: 'C: Content & Localization', icon: Icons.kb },
  { id: 'section-d', label: 'D: Reach & Engagement', icon: Icons.delivery },
  { id: 'section-e', label: 'E: Value Chains', icon: Icons.target },
  { id: 'section-f', label: 'F: Quality, Trust & Safety', icon: Icons.quality },
  { id: 'section-g', label: 'G: Cost & Sustainability', icon: Icons.cost },
  { id: 'section-h', label: 'H: Infrastructure & Policy', icon: Icons.policy },
  { id: 'section-ij', label: 'I-J: Interop & Outcomes', icon: Icons.api },
  { id: 'insights', label: 'AI Strategic Insights', icon: Icons.insights },
];

// ─── Chart helpers ────────────────────────────────────────────────────────────
function DoughnutChart({ data, labels }) {
  return (
    <Doughnut
      data={{
        labels,
        datasets: [{
          data,
          backgroundColor: PIE_COLORS.slice(0, data.length),
          borderWidth: 0,
          hoverOffset: 4,
        }],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        cutout: '65%',
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1F2937',
            titleFont: { size: 12 },
            bodyFont: { size: 12 },
            padding: 10,
            cornerRadius: 8,
            callbacks: { label: ctx => ` ${ctx.label}: ${ctx.raw}` },
          },
        },
      }}
    />
  );
}

function HBarChart({ data, labels, color = C.blue }) {
  // Truncate long labels for display, full text on hover via tooltip
  const truncated = labels.map(l => l.length > 35 ? l.slice(0, 35) + '…' : l);
  return (
    <Bar
      data={{
        labels: truncated,
        datasets: [{ data, backgroundColor: color, borderRadius: 6, barThickness: 18 }],
      }}
      options={{
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { left: 0 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1F2937', cornerRadius: 8, padding: 10,
            callbacks: { title: (ctx) => labels[ctx[0].dataIndex] },
          },
        },
        scales: {
          x: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { stepSize: 1, font: { size: 11 } }, border: { display: false } },
          y: {
            grid: { display: false },
            ticks: { font: { size: 11 }, autoSkip: false, padding: 4 },
            border: { display: false },
            afterFit: (axis) => { axis.width = Math.max(axis.width, 200); },
          },
        },
      }}
    />
  );
}

function VBarChart({ data, labels, colors }) {
  return (
    <Bar
      data={{
        labels,
        datasets: [{
          data,
          backgroundColor: colors || labels.map((_, i) => PIE_COLORS[i % PIE_COLORS.length]),
          borderRadius: 6,
          barThickness: 32,
        }],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { backgroundColor: '#1F2937', cornerRadius: 8, padding: 10 } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 11 }, autoSkip: false }, border: { display: false } },
          y: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { stepSize: 1, font: { size: 11 } }, border: { display: false } },
        },
      }}
    />
  );
}

function ChartLegend({ labels, colors }) {
  return (
    <div className="chart-legend">
      {labels.map((l, i) => (
        <div key={i} className="legend-item" title={l}>
          <span className="legend-dot" style={{ background: (colors || PIE_COLORS)[i] }} />
          <span>{l.length > 40 ? l.slice(0, 40) + '…' : l}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon, iconColor, badge }) {
  return (
    <div className="kpi-card">
      <div className="kpi-top">
        <div className={`kpi-icon ${iconColor || 'blue'}`}>{icon}</div>
        {badge && <span className={`kpi-badge ${badge.type || 'neutral'}`}>{badge.label}</span>}
      </div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-label">{label}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, sub, badge, children, legend, height = 220, full = false }) {
  return (
    <div className={full ? 'chart-card-full' : 'chart-card'}>
      <div className="chart-header">
        <div>
          <div className="chart-title">{title}</div>
          {sub && <div className="chart-sub">{sub}</div>}
        </div>
        {badge && <span className="chart-badge">{badge}</span>}
      </div>
      <div style={{ position: 'relative', height }}>{children}</div>
      {legend}
    </div>
  );
}

function SectionHeader({ id, title, desc }) {
  return (
    <div className="section-header" id={id}>
      <div className="section-title">{title}</div>
      {desc && <div className="section-desc">{desc}</div>}
    </div>
  );
}

function RatingBar({ label, value }) {
  const pct = (value / 5) * 100;
  const color = pct >= 70 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <div className="rating-row">
      <div className="rating-label">{label}</div>
      <div className="rating-bar-bg">
        <div className="rating-bar-fill" style={{ width: `${pct.toFixed(1)}%`, background: color }} />
      </div>
      <div className="rating-val">{value.toFixed(1)}</div>
    </div>
  );
}

const POLICY_BADGE = {
  'Fully Integrated': { bg: '#ECFDF5', color: '#059669' },
  'Aligned': { bg: '#ECFDF5', color: '#059669' },
  'Partially Integrated': { bg: '#FFFBEB', color: '#D97706' },
  'Aware': { bg: '#EFF6FF', color: '#2563EB' },
  'Unaware': { bg: '#FEF2F2', color: '#DC2626' },
};

function PolicyRow({ name, dist }) {
  return (
    <div className="policy-row">
      <div className="policy-name">{name}</div>
      <div className="policy-badges">
        {Object.entries(dist).map(([k, v]) => {
          const style = POLICY_BADGE[k] || { bg: '#F3F4F6', color: '#6B7280' };
          return (
            <span key={k} className="policy-badge" style={{ background: style.bg, color: style.color }}>
              {k} ({v})
            </span>
          );
        })}
      </div>
    </div>
  );
}

const INSIGHT_COLORS = {
  'CRITICAL RISK': { bg: '#FEF2F2', color: '#DC2626' },
  'GROWTH OPPORTUNITY': { bg: '#ECFDF5', color: '#059669' },
  'STRATEGIC PRIORITY': { bg: '#EFF6FF', color: '#2563EB' },
  'INVESTMENT NEEDED': { bg: '#FFFBEB', color: '#D97706' },
  'COMPETITIVE EDGE': { bg: '#F5F3FF', color: '#7C3AED' },
  // fallbacks for legacy/API types
  'MUST FIX': { bg: '#FEF2F2', color: '#DC2626' },
  'SHOULD ADD': { bg: '#FFFBEB', color: '#D97706' },
  'NICE TO HAVE': { bg: '#ECFDF5', color: '#059669' },
  'KEY FINDING': { bg: '#EFF6FF', color: '#2563EB' },
};

function InsightBlock({ type, title, text }) {
  const style = INSIGHT_COLORS[type] || INSIGHT_COLORS['KEY FINDING'];
  return (
    <div className="insight-block">
      <span className="insight-tag" style={{ background: style.bg, color: style.color }}>{type}</span>
      <div className="insight-text"><strong>{title}</strong> — {text}</div>
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar({ active, onNav }) {
  return (
    <nav className="sidebar">
      <div className="sidebar-section">
        <div className="sidebar-label">Dashboard</div>
        {NAV_ITEMS.map(item => (
          <button
            key={item.id}
            className={`sidebar-item ${active === item.id ? 'active' : ''}`}
            onClick={() => onNav(item.id)}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
      <div className="sidebar-footer">
        <div className="sidebar-footer-text">AgriEnhance Survey Analytics v1.0</div>
      </div>
    </nav>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [rows, setRows] = useState(null);
  const [filename, setFilename] = useState('');
  const [dragging, setDragging] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState('');
  const [insightsSource, setInsightsSource] = useState('local'); // 'local' or 'ai'
  const [apiKey, setApiKey] = useState('');
  const [activeNav, setActiveNav] = useState('overview');
  const fileRef = useRef();

  const processFile = useCallback((file) => {
    setFilename(file.name);
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'csv') {
      Papa.parse(file, {
        header: true, skipEmptyLines: true,
        complete: (res) => {
          setRows(res.data);
          setInsights(null);
          fetchInsights(res.data, false);
        },
      });
    } else if (['xlsx', 'xls'].includes(ext)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        setRows(data);
        setInsights(null);
        fetchInsights(data, false);
      };
      reader.readAsArrayBuffer(file);
    }
  }, [apiKey]); // eslint-disable-line

  const fetchInsights = async (data, forceAI = false) => {
    setInsightsLoading(true);
    setInsightsError('');
    try {
      // Always generate local insights first
      const local = generateLocalInsights(data);

      // If user provided an OpenAI key and wants AI insights, enhance with GPT
      const key = forceAI ? apiKey : '';
      if (key) {
        setInsightsSource('ai');
        const prompt = buildInsightPrompt(data);
        const res = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${key}`,
          },
          body: JSON.stringify({
            model: 'gpt-4o-mini',
            max_tokens: 2000,
            messages: [{ role: 'user', content: prompt }],
          }),
        });
        const json = await res.json();
        if (json.error) throw new Error(json.error.message);
        const raw = json.choices[0].message.content;
        const clean = raw.replace(/```json|```/g, '').trim();
        setInsights(JSON.parse(clean));
      } else {
        setInsightsSource('local');
        setInsights(local);
      }
    } catch (e) {
      // API failed — fall back to local insights
      const local = generateLocalInsights(data);
      setInsightsSource('local');
      setInsights(local);
      setInsightsError('AI enhancement failed: ' + e.message + '. Showing data-driven insights instead.');
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleNav = (id) => {
    setActiveNav(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Drag handlers
  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onDrop = (e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) processFile(e.dataTransfer.files[0]); };
  const onFileChange = (e) => { if (e.target.files[0]) processFile(e.target.files[0]); };
  const reset = () => { setRows(null); setFilename(''); setInsights(null); setInsightsError(''); fileRef.current.value = ''; setActiveNav('overview'); };

  // ── Render upload ──────────────────────────────────────────────────────────
  if (!rows) {
    return (
      <div className="app">
        <header className="app-header">
          <div className="header-logo">
            <span className="logo-mark">E</span>
            <div>
              <div className="header-brand">Ekansh Insight Engines</div>
              <div className="header-sub">AgriEnhance · Baseline Survey Analytics</div>
            </div>
          </div>
        </header>
        <main className="upload-main">
          <div
            className={`upload-zone ${dragging ? 'drag' : ''}`}
            onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
          >
            <div className="upload-icon">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="8" y="10" width="32" height="30" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M16 24l8-8 8 8M24 16v18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="upload-title">Upload your baseline survey responses</div>
            <div className="upload-sub">CSV or Excel (.xlsx / .xls) exported from your Google Form or survey tool</div>
            <label className="upload-btn">
              Browse file
              <input type="file" accept=".csv,.xlsx,.xls" ref={fileRef} onChange={onFileChange} style={{ display: 'none' }} />
            </label>
          </div>
          <div className="api-key-section">
            <label className="api-key-label">OpenAI API key (optional — for AI-enhanced insights)</label>
            <input
              type="password"
              className="api-key-input"
              placeholder="sk-..."
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
            />
            <div className="api-key-hint">Optional. Data-driven insights work without a key. Your key stays in your browser and is never stored on any server.</div>
          </div>
        </main>
      </div>
    );
  }

  // ── Compute data ───────────────────────────────────────────────────────────
  const metrics = computeMetrics(rows);
  const ratings = computeRatings(rows);
  const policies = computePolicies(rows);

  // Section A: Organization Profile
  const orgTypeEntries = topN(countValues(rows, ['organization type']), 6);
  const roleEntries = topN(countValues(rows, ['role/title']), 6);
  const aiOfferEntries = topN(countValues(rows, ['currently offer ai']), 3);

  // Section B: AI Models & Delivery
  const modelEntries = topN(countValues(rows, ['ai advisory model']), 6);
  const llmEntries = topN(countValues(rows, ['large language model']), 8);
  const hostEntries = topN(countValues(rows, ['hosting model']), 5);
  const maturityEntries = topN(countValues(rows, ['maturity stage']), 5);
  const channelEntries = topN(countValues(rows, ['delivery channels']), 10);
  const userEntries = topN(countValues(rows, ['primary user of the advisory']), 6);

  // Section C: Content & Localization
  const kbUsageEntries = topN(countValues(rows, ['knowledge base/content']), 3);
  const kbStructEntries = topN(countValues(rows, ['knowledge base structured']), 5);
  const srcEntries = topN(countValues(rows, ['primary sources of advisory']), 8);
  const langEntries = topN(countValues(rows, ['languages used for advisory']), 6);
  const multilingEntries = topN(countValues(rows, ['multilingual delivery']), 5);
  const localizEntries = topN(countValues(rows, ['localization handled']), 8);
  const speechEntries = topN(countValues(rows, ['speech-to-text']), 3);

  // Section D: Reach & Engagement
  const engagementEntries = topN(countValues(rows, ['engagement frequency']), 5);
  const demoEntries = topN(countValues(rows, ['demographics tracked']), 8);

  // Section E: Value Chains
  const cropEntries = topN(countValues(rows, ['crops currently covered']), 15);
  const livestockEntries = topN(countValues(rows, ['livestock systems']), 8);
  const focusEntries = topN(countValues(rows, ['advisory focus areas']), 12);

  // Section F: Quality, Trust & Safety
  const qualityAssessEntries = topN(countValues(rows, ['assess the quality']), 3);
  const contentEvidEntries = topN(countValues(rows, ['evidence is currently available on advisory content']), 8);
  const accCounts = countValues(rows, ['estimated response accuracy']);
  const accOrder = ['Not measured', '50% and below', '51 - 70%', '71 - 90%', 'Above 90%', '91 - 100%'];
  const accData = accOrder.filter(k => accCounts[k]).map(k => accCounts[k]);
  const accLabels = accOrder.filter(k => accCounts[k]);
  const evalEntries = topN(countValues(rows, ['ai evaluation framework']), 8);
  const safetyRuleEntries = topN(countValues(rows, ['safety rules do you enforce']), 10);
  const trustEntries = topN(countValues(rows, ['monitor farmer trust']), 6);
  const safetyPracticeEntries = topN(countValues(rows, ['safety and risk management']), 10);

  // Section G: Cost & Sustainability
  const costTrackEntries = topN(countValues(rows, ['costs of ai advisory']), 3);
  const costMetricEntries = topN(countValues(rows, ['indicative cost metrics']), 8);
  const sustEntries = topN(countValues(rows, ['sustainability model']), 6);

  // Section H: Infrastructure & Policy
  const infraEntries = topN(countValues(rows, ['infrastructure constraints']), 11);

  // Section I-J: Interop & Outcomes
  const apiEntries = topN(countValues(rows, ['apis or integration']), 4);
  const outcomeTrackEntries = topN(countValues(rows, ['track farmer-level']), 4);
  const outcomeEntries = topN(countValues(rows, ['outcome evidence']), 6);

  const pctAI = Math.round(metrics.hasAI / metrics.n * 100);
  const pctKB = Math.round(metrics.hasKB / metrics.n * 100);
  const pctOutcomes = Math.round(metrics.hasOutcomes / metrics.n * 100);
  const pctAPI = Math.round(metrics.hasAPI / metrics.n * 100);

  // ── Render dashboard ──────────────────────────────────────────────────────
  return (
    <div className="app">
      <header className="app-header">
        <div className="header-logo">
          <span className="logo-mark">E</span>
          <div>
            <div className="header-brand">Ekansh Insight Engines</div>
            <div className="header-sub">AgriEnhance · Baseline Survey Analytics</div>
          </div>
        </div>
        <div className="header-right">
          <span className="file-chip">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            {filename} · {rows.length} responses
          </span>
          <button className="reset-btn" onClick={reset}>Upload new file</button>
        </div>
      </header>

      <div className="app-body">
        <Sidebar active={activeNav} onNav={handleNav} />

        <main className="dashboard">

          {/* ══ OVERVIEW ══════════════════════════════════════════ */}
          <SectionHeader id="overview" title="Overview" desc={`${metrics.n} organizations surveyed · Baseline metrics at a glance`} />
          <div className="kpi-grid">
            <KpiCard icon={Icons.users} iconColor="blue" label="Total Respondents" value={metrics.n} sub="Organizations surveyed" />
            <KpiCard icon={Icons.target} iconColor="green" label="Offer AI Advisory" value={metrics.hasAI} sub={`${pctAI}% of respondents`} badge={{ label: `${pctAI}%`, type: pctAI >= 50 ? 'up' : 'neutral' }} />
            <KpiCard icon={Icons.users} iconColor="purple" label="Total Farmers Reached" value={metrics.totalFarmers.toLocaleString()} sub="Cumulative across organizations" />
            <KpiCard icon={Icons.kb} iconColor="orange" label="Use Knowledge Base" value={metrics.hasKB} sub={`${pctKB}% of respondents`} badge={{ label: `${pctKB}%`, type: pctKB >= 50 ? 'up' : 'neutral' }} />
          </div>
          <div className="kpi-grid">
            <KpiCard icon={Icons.check} iconColor="green" label="Track Farmer Outcomes" value={metrics.hasOutcomes} sub={`${pctOutcomes}% of respondents`} badge={{ label: `${pctOutcomes}%`, type: pctOutcomes >= 50 ? 'up' : 'neutral' }} />
            <KpiCard icon={Icons.cost} iconColor="orange" label="Track Delivery Costs" value={metrics.costTracked} sub={`${Math.round(metrics.costTracked / metrics.n * 100)}% of respondents`} />
            <KpiCard icon={Icons.api} iconColor="blue" label="Have API Capability" value={metrics.hasAPI} sub={`${pctAPI}% of respondents`} badge={{ label: `${pctAPI}%`, type: pctAPI >= 40 ? 'up' : 'down' }} />
            <KpiCard icon={Icons.quality} iconColor="purple" label="Assess Content Quality" value={metrics.assessQuality} sub={`${Math.round(metrics.assessQuality / metrics.n * 100)}% of respondents`} />
          </div>

          {/* ══ SECTION A: Organization Profile ══════════════════ */}
          <SectionHeader id="section-a" title="Section A: Respondent & Organization Profile" desc="Who responded to the survey — organization types and roles" />
          <div className="chart-grid">
            <ChartCard title="Organization Type" sub="Distribution of surveyed organizations" legend={<ChartLegend labels={orgTypeEntries.map(e => e[0])} />}>
              <DoughnutChart data={orgTypeEntries.map(e => e[1])} labels={orgTypeEntries.map(e => e[0])} />
            </ChartCard>
            <ChartCard title="Respondent Roles" sub="Who filled in the survey" legend={<ChartLegend labels={roleEntries.map(e => e[0])} />}>
              <DoughnutChart data={roleEntries.map(e => e[1])} labels={roleEntries.map(e => e[0])} />
            </ChartCard>
          </div>
          <div className="chart-grid">
            <ChartCard title="Currently Offer AI Advisory?" sub="Q6: AI-enabled services status" legend={<ChartLegend labels={aiOfferEntries.map(e => e[0])} />}>
              <DoughnutChart data={aiOfferEntries.map(e => e[1])} labels={aiOfferEntries.map(e => e[0])} />
            </ChartCard>
            <ChartCard title="AI System Maturity Stage" sub="Q9: Among those offering AI" legend={<ChartLegend labels={maturityEntries.map(e => e[0])} />}>
              <DoughnutChart data={maturityEntries.map(e => e[1])} labels={maturityEntries.map(e => e[0])} />
            </ChartCard>
          </div>

          {/* ══ SECTION B: AI Models & Delivery ══════════════════ */}
          <SectionHeader id="section-b" title="Section B: AI Advisory Models & Delivery Channels" desc="Technology stack, LLMs, and how advisory is delivered" />
          <div className="chart-grid">
            <ChartCard title="AI Model Types Used" sub="Q10: Select all that apply" badge={`${modelEntries.length} types`} legend={<ChartLegend labels={modelEntries.map(e => e[0])} />}>
              <DoughnutChart data={modelEntries.map(e => e[1])} labels={modelEntries.map(e => e[0])} />
            </ChartCard>
            <ChartCard title="LLMs in Use" sub="Q12: Select all that apply" badge={`${llmEntries.length} models`} legend={<ChartLegend labels={llmEntries.map(e => e[0])} />}>
              <DoughnutChart data={llmEntries.map(e => e[1])} labels={llmEntries.map(e => e[0])} />
            </ChartCard>
          </div>
          <div className="chart-grid">
            <ChartCard title="Delivery Channels" sub="Q13: Select all that apply" height={Math.max(200, channelEntries.length * 38 + 30)}>
              <HBarChart data={channelEntries.map(e => e[1])} labels={channelEntries.map(e => e[0])} color={C.blue} />
            </ChartCard>
            <ChartCard title="Primary Users" sub="Q15: Who uses the advisory service" height={Math.max(200, userEntries.length * 38 + 30)}>
              <HBarChart data={userEntries.map(e => e[1])} labels={userEntries.map(e => e[0])} color={C.purple} />
            </ChartCard>
          </div>
          <div className="chart-grid">
            <ChartCard title="Primary Hosting Model" sub="Q18: Infrastructure type" legend={<ChartLegend labels={hostEntries.map(e => e[0])} />}>
              <DoughnutChart data={hostEntries.map(e => e[1])} labels={hostEntries.map(e => e[0])} />
            </ChartCard>
          </div>

          {/* ══ SECTION C: Content & Localization ════════════════ */}
          <SectionHeader id="section-c" title="Section C: Advisory Content & Localization" desc="Knowledge bases, content sources, languages, and localization" />
          <div className="chart-grid">
            <ChartCard title="Knowledge Base Usage" sub="Q16: Does the system use a KB?" legend={<ChartLegend labels={kbUsageEntries.map(e => e[0])} />}>
              <DoughnutChart data={kbUsageEntries.map(e => e[1])} labels={kbUsageEntries.map(e => e[0])} />
            </ChartCard>
            <ChartCard title="KB Structure" sub="Q17: How is the KB structured?" legend={<ChartLegend labels={kbStructEntries.map(e => e[0])} />}>
              <DoughnutChart data={kbStructEntries.map(e => e[1])} labels={kbStructEntries.map(e => e[0])} />
            </ChartCard>
          </div>
          <div className="chart-grid">
            <ChartCard title="Advisory Content Sources" sub="Q19: Select all that apply" height={Math.max(200, srcEntries.length * 38 + 30)}>
              <HBarChart data={srcEntries.map(e => e[1])} labels={srcEntries.map(e => e[0])} color={C.green} />
            </ChartCard>
            <ChartCard title="Languages for Delivery" sub="Q20: Select all that apply" legend={<ChartLegend labels={langEntries.map(e => e[0])} />}>
              <DoughnutChart data={langEntries.map(e => e[1])} labels={langEntries.map(e => e[0])} />
            </ChartCard>
          </div>
          <div className="chart-grid">
            <ChartCard title="Multilingual Approach" sub="Q22: How is multilingual delivery handled?" legend={<ChartLegend labels={multilingEntries.map(e => e[0])} />}>
              <DoughnutChart data={multilingEntries.map(e => e[1])} labels={multilingEntries.map(e => e[0])} />
            </ChartCard>
            <ChartCard title="Localization Methods" sub="Q25: How is localization handled?" height={Math.max(200, localizEntries.length * 38 + 30)}>
              <HBarChart data={localizEntries.map(e => e[1])} labels={localizEntries.map(e => e[0])} color={C.teal} />
            </ChartCard>
          </div>
          <div className="chart-grid">
            <ChartCard title="Speech-to-Text / Text-to-Speech" sub="Q23: Voice capability" legend={<ChartLegend labels={speechEntries.map(e => e[0])} />}>
              <DoughnutChart data={speechEntries.map(e => e[1])} labels={speechEntries.map(e => e[0])} />
            </ChartCard>
          </div>

          {/* ══ SECTION D: Reach & Engagement ════════════════════ */}
          <SectionHeader id="section-d" title="Section D: Reach, Engagement & Coverage" desc="Farmer reach, engagement frequency, demographics, and county coverage" />
          <div className="chart-grid">
            <ChartCard title="Engagement Frequency" sub="Q27: How often users returned (last 3 months)" legend={<ChartLegend labels={engagementEntries.map(e => e[0])} />}>
              <DoughnutChart data={engagementEntries.map(e => e[1])} labels={engagementEntries.map(e => e[0])} />
            </ChartCard>
            <ChartCard title="Demographics Tracked" sub="Q31: Select all that apply" height={Math.max(200, demoEntries.length * 38 + 30)}>
              <HBarChart data={demoEntries.map(e => e[1])} labels={demoEntries.map(e => e[0])} color={C.indigo} />
            </ChartCard>
          </div>

          {/* ══ SECTION E: Value Chains & Farming Systems ════════ */}
          <SectionHeader id="section-e" title="Section E: Value Chains & Farming Systems" desc="Crops, livestock, and advisory focus areas covered" />
          <div className="chart-grid">
            <ChartCard title="Crops Covered" sub="Q35: Select all that apply" height={Math.max(200, cropEntries.length * 34 + 30)}>
              <HBarChart data={cropEntries.map(e => e[1])} labels={cropEntries.map(e => e[0])} color={C.green} />
            </ChartCard>
            <ChartCard title="Livestock Systems" sub="Q36: Select all that apply" height={Math.max(200, livestockEntries.length * 38 + 30)}>
              <HBarChart data={livestockEntries.map(e => e[1])} labels={livestockEntries.map(e => e[0])} color={C.amber} />
            </ChartCard>
          </div>
          <ChartCard title="Advisory Focus Areas / Topics" sub="Q37: Select all that apply" full height={Math.max(250, focusEntries.length * 34 + 30)}>
            <HBarChart data={focusEntries.map(e => e[1])} labels={focusEntries.map(e => e[0])} color={C.blue} />
          </ChartCard>

          {/* ══ SECTION F: Quality, Trust & Safety ════════════════ */}
          <SectionHeader id="section-f" title="Section F: Quality, Trust & Safety" desc="Content quality, accuracy, evaluation frameworks, and safety practices" />
          <div className="chart-grid">
            <ChartCard title="Assess Content Quality?" sub="Q38: Does your org assess quality?" legend={<ChartLegend labels={qualityAssessEntries.map(e => e[0])} />}>
              <DoughnutChart data={qualityAssessEntries.map(e => e[1])} labels={qualityAssessEntries.map(e => e[0])} />
            </ChartCard>
            <ChartCard title="Estimated Response Accuracy" sub="Q45: Self-reported accuracy rate">
              <VBarChart data={accData} labels={accLabels} colors={accLabels.map(l => l === 'Not measured' ? C.gray : l === '50% and below' ? C.coral2 : l === '51 - 70%' ? C.amber2 : l === '71 - 90%' ? C.green2 : C.teal)} />
            </ChartCard>
          </div>
          <div className="chart-grid">
            <ChartCard title="Content Quality Evidence" sub="Q39: What evidence is available?" height={Math.max(200, contentEvidEntries.length * 34 + 30)}>
              <HBarChart data={contentEvidEntries.map(e => e[1])} labels={contentEvidEntries.map(e => e[0])} color={C.green} />
            </ChartCard>
            <ChartCard title="AI Evaluation Frameworks" sub="Q40: Select all that apply" height={Math.max(200, evalEntries.length * 34 + 30)}>
              <HBarChart data={evalEntries.map(e => e[1])} labels={evalEntries.map(e => e[0])} color={C.purple} />
            </ChartCard>
          </div>
          <div className="chart-grid">
            <ChartCard title="Safety Rules Enforced" sub="Q42: Select all that apply" height={Math.max(200, safetyRuleEntries.length * 34 + 30)}>
              <HBarChart data={safetyRuleEntries.map(e => e[1])} labels={safetyRuleEntries.map(e => e[0])} color={C.coral} />
            </ChartCard>
            <ChartCard title="Trust & Confidence Monitoring" sub="Q46: How do you monitor trust?" height={Math.max(200, trustEntries.length * 38 + 30)}>
              <HBarChart data={trustEntries.map(e => e[1])} labels={trustEntries.map(e => e[0])} color={C.teal} />
            </ChartCard>
          </div>
          <ChartCard title="AI Safety & Risk Management Practices" sub="Q47: Select all that apply" full height={Math.max(250, safetyPracticeEntries.length * 34 + 30)}>
            <HBarChart data={safetyPracticeEntries.map(e => e[1])} labels={safetyPracticeEntries.map(e => e[0])} color={C.navy} />
          </ChartCard>
          <div className="chart-card-full">
            <div className="chart-header">
              <div>
                <div className="chart-title">Q48: Perceived Performance by Dimension</div>
                <div className="chart-sub">Self-rated 1–5 scale · Green ≥ 3.5 · Amber ≥ 2.5 · Red below 2.5</div>
              </div>
              <span className="chart-badge">{ratings.length} dimensions</span>
            </div>
            <div className="ratings-container">
              {ratings.map((r, i) => <RatingBar key={i} label={r.label} value={r.avg} />)}
            </div>
          </div>

          {/* ══ SECTION G: Cost & Sustainability ══════════════════ */}
          <SectionHeader id="section-g" title="Section G: Cost & Sustainability" desc="Cost tracking, unit economics, and funding models" />
          <div className="chart-grid">
            <ChartCard title="Are Delivery Costs Tracked?" sub="Q49: Cost tracking status" legend={<ChartLegend labels={costTrackEntries.map(e => e[0])} />}>
              <DoughnutChart data={costTrackEntries.map(e => e[1])} labels={costTrackEntries.map(e => e[0])} />
            </ChartCard>
            <ChartCard title="Cost Metrics Available" sub="Q50: What cost data exists?" height={Math.max(200, costMetricEntries.length * 38 + 30)}>
              <HBarChart data={costMetricEntries.map(e => e[1])} labels={costMetricEntries.map(e => e[0])} color={C.orange} />
            </ChartCard>
          </div>
          <div className="chart-grid">
            <ChartCard title="Sustainability Models" sub="Q51: Current funding structures" legend={<ChartLegend labels={sustEntries.map(e => e[0])} />}>
              <DoughnutChart data={sustEntries.map(e => e[1])} labels={sustEntries.map(e => e[0])} />
            </ChartCard>
          </div>

          {/* ══ SECTION H: Infrastructure & Policy ════════════════ */}
          <SectionHeader id="section-h" title="Section H: Capacity, Infrastructure & Policy" desc="Scaling barriers and policy alignment" />
          <ChartCard title="Key Infrastructure Constraints" sub="Q52: Barriers to scaling AI advisory" full height={Math.max(250, infraEntries.length * 36 + 30)}>
            <HBarChart data={infraEntries.map(e => e[1])} labels={infraEntries.map(e => e[0])} color={C.amber} />
          </ChartCard>
          <div className="chart-card-full">
            <div className="chart-header">
              <div>
                <div className="chart-title">Q53: Policy Alignment Distribution</div>
                <div className="chart-sub">Awareness and alignment with key Kenya policies</div>
              </div>
              <span className="chart-badge">{policies.length} policies</span>
            </div>
            <div className="policy-grid">
              {policies.map((p, i) => <PolicyRow key={i} name={p.name} dist={p.dist} />)}
            </div>
          </div>

          {/* ══ SECTION I-J: Interoperability & Outcomes ══════════ */}
          <SectionHeader id="section-ij" title="Sections I & J: Interoperability & Farmer Outcomes" desc="API capabilities, data sharing, and farmer-level outcomes" />
          <div className="chart-grid">
            <ChartCard title="API / Integration Capability" sub="Q54: Does your system have APIs?" legend={<ChartLegend labels={apiEntries.map(e => e[0])} />}>
              <DoughnutChart data={apiEntries.map(e => e[1])} labels={apiEntries.map(e => e[0])} />
            </ChartCard>
            <ChartCard title="Track Farmer-Level Outcomes?" sub="Q55: Outcome tracking status" legend={<ChartLegend labels={outcomeTrackEntries.map(e => e[0])} />}>
              <DoughnutChart data={outcomeTrackEntries.map(e => e[1])} labels={outcomeTrackEntries.map(e => e[0])} />
            </ChartCard>
          </div>
          <div className="chart-grid">
            <ChartCard title="Outcome Evidence Available" sub="Q56: What outcome data exists?" legend={<ChartLegend labels={outcomeEntries.map(e => e[0])} />}>
              <DoughnutChart data={outcomeEntries.map(e => e[1])} labels={outcomeEntries.map(e => e[0])} />
            </ChartCard>
          </div>

          {/* ── AI Insights ──────────────────────────────────────── */}
          <SectionHeader id="insights" title="AI Strategic Insights" desc="Business-decision intelligence generated from survey data" />
          <div className="insights-box">
            <div className="insights-header">
              <div className="insights-title">Strategic Analysis & Recommendations</div>
              <span className="ai-badge" style={insightsSource === 'local' ? { background: 'var(--green-bg)', color: 'var(--green)' } : {}}>
                {insightsSource === 'ai' ? (
                  <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg> AI GENERATED</>
                ) : (
                  <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> DATA-DRIVEN</>
                )}
              </span>
            </div>
            {insightsLoading && (
              <div className="ai-loading">
                <div className="spinner" />
                <div className="ai-loading-text">Generating insights across {rows.length} survey responses…</div>
              </div>
            )}
            {insightsError && !insightsLoading && (
              <div className="error-box" style={{ marginBottom: 16 }}>{insightsError}</div>
            )}
            {insights && !insightsLoading && insights.map((ins, i) => (
              <InsightBlock key={i} type={ins.type} title={ins.title} text={ins.text} />
            ))}
            {!insightsLoading && (
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Enhance with AI</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>Enter your OpenAI API key to generate deeper AI-powered strategic insights. Your key is used only in your browser and never stored on any server.</div>
                <div className="api-inline">
                  <input
                    type="password"
                    className="api-key-input"
                    placeholder="sk-..."
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    style={{ maxWidth: 320 }}
                  />
                  <button className="generate-btn" disabled={!apiKey} onClick={() => fetchInsights(rows, true)}>Generate AI Insights</button>
                </div>
              </div>
            )}
          </div>

        </main>
      </div>

      <footer className="app-footer">
        <span>Ekansh Insight Engines · AgriEnhance Kenya · Baseline Survey Analytics</span>
        <span>Powered by AI</span>
      </footer>
    </div>
  );
}
